<template>
    <div class="editorView" ref="editorViewHeight">
        <TransitionEffect>
            <Dialog
                v-if="deleteEntryDialog"
                appearance="red"
                primaryIcon="delete"
                @primary="deleteEntry"
                @secondary="deleteEntryDialog=false"
                @close="deleteEntryDialog=false">
                <template slot="header">{{ siteContent.deleteEntryQuestion }}</template>
                <template slot="body">{{ siteContent.thinkTwice }}</template>
                <template slot="secondary">{{ siteContent.cancel }}</template>
                <template slot="primary">{{ siteContent.delete }}</template>
            </Dialog>
        </TransitionEffect>

        <LoadingOverlay
            v-if="destroy.status"
            :indefinite="true"/>

        <div class="toggleView"
            v-if="preferences.language === 'hu'">
            <a :class="['toggleItem', { isToggleActive: isEnglishVisible }]"
                @click="toggleActiveView"
                tabindex="0">
                {{ siteContent.english}}
            </a>
            <a :class="['toggleItem', { isToggleActive: !isEnglishVisible }]"
                @click="toggleActiveView"
                tabindex="0">
                {{ siteContent.hungarian }}
            </a>
        </div>

        <div class="localizedFieldsGroup" ref="localizedFieldsGroup">
            <TransitionEffect name="fadeHorizontally">
                <div class="localizedFields" ref="englishFields" v-show="isEnglishVisible">
                    <div class="relatedFields">
                        <div class="horizontalFields detailInput">
                            <TextField class="title"
                                v-model="currentArtwork.content.title.en"
                                id="titleEn"
                                ref="titleEditEn"
                                font="secondary"
                                size="large"
                                :label="siteContent.title"
                                :maxlength="constants.limits.entry.title"/>

                            <TextField class="title"
                                v-model="currentArtwork.content.subtitle.en"
                                id="subtitleEn"
                                ref="subtitleEditEn"
                                font="secondary"
                                size="large"
                                :label="siteContent.subtitle"
                                :maxlength="constants.limits.entry.subtitle"/>
                        </div>
                        <TextField class="detailInput"
                            v-model="currentArtwork.content.intro.en"
                            id="introEn"
                            ref="introEditEn"
                            type="textarea"
                            size="medium"
                            :label="siteContent.intro"
                            :maxlength="constants.limits.entry.intro"
                            :isCharCountOn="true"
                            @keyup="emitEditorViewHeight">{{ currentArtwork.content.intro.en }}</TextField>

                        <TextField class="detailInput"
                            v-model="currentArtwork.content.story.en"
                            id="storyEn"
                            ref="storyEditEn"
                            type="textarea"
                            :label="siteContent.story"
                            :maxlength="constants.limits.entry.story"
                            :isCharCountOn="true"
                            @keyup="emitEditorViewHeight">{{ currentArtwork.content.story.en }}</TextField>
                    </div>
                </div>
            </TransitionEffect>

            <TransitionEffect name="fadeHorizontally">
                <div class="localizedFields" ref="hungarianFields" v-show="!isEnglishVisible">
                    <div class="relatedFields">
                        <div class="horizontalFields detailInput">
                            <TextField class="title"
                                v-model="currentArtwork.content.title.hu"
                                id="titleHu"
                                ref="titleEditHu"
                                font="secondary"
                                size="large"
                                :label="siteContent.title"
                                :maxlength="constants.limits.entry.title"/>

                            <TextField class="title"
                                v-model="currentArtwork.content.subtitle.hu"
                                id="subtitleHu"
                                ref="subtitleEditHu"
                                font="secondary"
                                size="large"
                                :label="siteContent.subtitle"
                                :maxlength="constants.limits.entry.subtitle"/>
                        </div>
                        <TextField class="detailInput"
                            v-model="currentArtwork.content.intro.hu"
                            id="introHu"
                            ref="introEditHu"
                            type="textarea"
                            size="medium"
                            :label="siteContent.intro"
                            :maxlength="constants.limits.entry.intro"
                            :isCharCountOn="true"
                            @keyup="emitEditorViewHeight">{{ currentArtwork.content.intro.hu }}</TextField>

                        <TextField class="detailInput"
                            v-model="currentArtwork.content.story.hu"
                            id="storyHu"
                            ref="storyEditHu"
                            type="textarea"
                            :label="siteContent.story"
                            :maxlength="constants.limits.entry.story"
                            :isCharCountOn="true"
                            @keyup="emitEditorViewHeight">{{ currentArtwork.content.story.hu }}</TextField>
                    </div>
                </div>
            </TransitionEffect>
        </div>

        <DatePicker class="detailInput"
            :date="currentArtwork.activity.historicalTime"
            :label="siteContent.historicalTime"
            :description="siteContent.historicalTimeDescription"
            emitName="setNewHistoricalTime"
            @setNewHistoricalTime="setNewHistoricalTime"/>

        <div class="relatedFields"
            v-if="permissions.isAdmin">
            <DatePicker class="detailInput"
                :date="currentArtwork.activity.uploadTime"
                :label="siteContent.uploadTime"
                emitName="setNewUploadTime"
                @setNewUploadTime="setNewUploadTime"/>
            <TextField class="detailInput slugField"
                v-model="currentArtwork.extra.relatedPost"
                id="relatedPost"
                ref="relatedPostEdit"
                size="large"
                :label="siteContent.relatedPost"/>
        </div>

        <div class="toggleTile first">
            <Toggle
                v-model="currentArtwork.flag.visibility"
                ref="visibilityEdit"
                :label="siteContent.publishEntry">
            </Toggle>
        </div>
        <div class="toggleTile"
            v-if="permissions.isAdmin">
            <Toggle
                v-model="currentArtwork.flag.featured"
                ref="featuredEdit"
                :label="siteContent.featuredEntry">
            </Toggle>
        </div>

        <TransitionEffect>
            <div class="buttonsWrapper">
                <div class="buttons">
                    <Button class="deleteEntryButton"
                        @click="deleteEntryDialog=true"
                        prefixIcon="delete"
                        appearance="red"
                        size="small">
                        <span class="deleteText">
                            {{ siteContent.delete }}
                        </span>
                    </Button>

                    <div class="editEntryOptions">
                        <Button class="cancelEditButton"
                            @click="cancelEdit"
                            prefixIcon="arrow_left"
                            appearance="secondary"
                            size="small">
                            {{ siteContent.cancel }}
                        </Button>

                        <Button
                            @click="updateEntry"
                            prefixIcon="save"
                            size="small">
                            {{ siteContent.update }}
                        </Button>
                    </div>
                </div>
            </div>
        </TransitionEffect>
    </div>
</template>

<script>
    import { db } from '@/firebase'
    import { mapActions } from 'vuex'

    export default {
        name: 'EditorView',
        props: {
            currentArtwork: Object,
            currentEntryId: String,
        },
        data() {
            return {
                isEnglishVisible: true,
                deleteEntryDialog: false,
                editorViewHeight: 0,
                destroy: {
                    status: false,
                    progress: 0
                },

                title: { 
                    en: this.currentArtwork.content.title.en,
                    hu: this.currentArtwork.content.title.hu
                },
                subtitle: { 
                    en: this.currentArtwork.content.subtitle.en,
                    hu: this.currentArtwork.content.subtitle.hu
                },
                intro: {
                    en: this.currentArtwork.content.intro.en,
                    hu: this.currentArtwork.content.intro.hu
                },
                story: {
                    en: this.currentArtwork.content.story.en,
                    hu: this.currentArtwork.content.story.hu
                },
                storywriter:           this.currentArtwork.creator.storywriter,
                uploadTime:            this.currentArtwork.activity.uploadTime,
                historicalTime:        this.currentArtwork.activity.historicalTime,
                initialPublish:        this.currentArtwork.activity.initialPublish,
                visibility:            this.currentArtwork.flag.visibility,
                featured:              this.currentArtwork.flag.featured,
                relatedPost:           this.currentArtwork.extra.relatedPost,
                updatedUploadTime:     null,
                updatedHistoricalTime: null,
            }
        },
        async mounted() {
            this.forceUpdateTextFields()
            this.$nextTick(() => {
                this.emitEditorViewHeight()
            })

            if(this.preferences.language === 'en') {
                this.isEnglishVisible = true
            } else {
                this.isEnglishVisible = false
            }
        },
        methods: {
            ...mapActions('journal',['getArtworkDataById','updateJournalEntry','deleteJournalEntry']),
            ...mapActions('notifications',['createNotification','deleteNotification']),

            setNewHistoricalTime(e) {
                this.updatedHistoricalTime = e
            },

            setNewUploadTime(e) {
                this.updatedUploadTime = e
            },

            toggleActiveView() {
                this.isEnglishVisible = !this.isEnglishVisible
                this.$nextTick(() => {
                    this.emitEditorViewHeight()
                })
            },

            cancelEdit() {
                this.$nextTick(async () => {
                    this.currentArtwork.content.title            = this.title
                    this.currentArtwork.content.subtitle         = this.subtitle
                    this.currentArtwork.content.intro            = this.intro
                    this.currentArtwork.content.story            = this.story
                    this.currentArtwork.activity.uploadTime      = this.uploadTime
                    this.currentArtwork.activity.historicalTime  = this.historicalTime
                    this.currentArtwork.activity.initialPublish  = this.initialPublish
                    this.currentArtwork.flag.visibility          = this.visibility
                    this.currentArtwork.creator.storywriter      = this.storywriter
                    this.currentArtwork.extra.relatedPost        = this.relatedPost
                })

                this.$emit('finishEditing')
            },

            forceUpdateTextFields() {
                const prologueElementEn = this.$refs.introEditEn.$el.querySelector('textarea')
                const prologueElementHu = this.$refs.introEditHu.$el.querySelector('textarea')
                const epilogueElementEn = this.$refs.storyEditEn.$el.querySelector('textarea')
                const epilogueElementHu = this.$refs.storyEditHu.$el.querySelector('textarea')
                const event = new Event('keyup')

                epilogueElementHu.dispatchEvent(event)
                epilogueElementEn.dispatchEvent(event)
                prologueElementHu.dispatchEvent(event)
                prologueElementEn.dispatchEvent(event)
            },

            setLocalizedFieldsGroupHeight() {
                if(this.isEnglishVisible === true) {
                    this.$refs.localizedFieldsGroup.setAttribute("style",`height:${this.$refs.englishFields.clientHeight}px`)
                } else {
                    this.$refs.localizedFieldsGroup.setAttribute("style",`height:${this.$refs.hungarianFields.clientHeight}px`)
                }
            },

            calculateEditorViewHeight() {
                this.editorViewHeight = this.$refs.editorViewHeight.clientHeight
            },

            emitEditorViewHeight() {
                this.$nextTick(() => {
                    this.setLocalizedFieldsGroupHeight()
                    this.calculateEditorViewHeight()
                    this.$emit('setEditorViewHeight', this.editorViewHeight)
                })
            },

            async updateEntry() {
                this.$nextTick(async () => {
                    var eventId = this.currentEntryId
                    var event = await this.getArtworkDataById(eventId)
                    if(event !== null) {
                        var updatedTitle = { 
                            hu: this.$refs.titleEditHu.value,
                            en: this.$refs.titleEditEn.value,
                        }
                        var updatedSubtitle = { 
                            hu: this.$refs.subtitleEditHu.value,
                            en: this.$refs.subtitleEditEn.value,
                        }
                        var updatedIntro = { 
                            hu: this.$refs.introEditHu.value,
                            en: this.$refs.introEditEn.value,
                        }
                        var updatedStory = { 
                            hu: this.$refs.storyEditHu.value,
                            en: this.$refs.storyEditEn.value,
                        }

                        var updatedVisibility  = this.$refs.visibilityEdit.isEnabled
                    
                        event.content.title      = updatedTitle
                        event.content.subtitle   = updatedSubtitle
                        event.content.intro      = updatedIntro
                        event.content.story      = updatedStory
                        event.flag.visibility    = updatedVisibility
                        
                        if(this.updatedHistoricalTime) {
                            event.activity.historicalTime = this.updatedHistoricalTime
                        }

                        if(updatedVisibility) {
                            event.activity.initialPublish = true
                        }

                        if(this.permissions.isAdmin) {
                            var updatedFeatured    = this.$refs.featuredEdit.isEnabled
                            var updatedRelatedPost = this.$refs.relatedPostEdit.value

                            event.flag.featured        = updatedFeatured
                            event.extra.relatedPost    = updatedRelatedPost

                            if(this.updatedUploadTime) {
                                event.activity.uploadTime = this.updatedUploadTime
                            }
                        }

                        var entry = {
                            id: eventId,
                            data: event
                        }

                        var entryID = await this.updateJournalEntry(entry)

                        if(entryID) {
                            if(this.currentArtwork.activity.initialPublish === false && this.$refs.visibilityEdit.isEnabled) {
                                try {
                                    (async () => {
                                        const followersRef = await db
                                            .collection('users')
                                            .doc(this.auth.id)
                                            .collection('social')
                                            .doc('followers')
                                            .get()

                                        var followers = {
                                            ...followersRef.data()
                                        }

                                        let notification = {
                                            type:       'newEntry',
                                            docID:       entryID,
                                            title:       this.currentArtwork.content.title,
                                            createdBy:   this.auth.id,
                                            timestamp:   new Date(),
                                            recipients:  followers.users
                                        }
                                        
                                        await this.createNotification({
                                            notification: notification
                                        })
                                    })()
                                } catch(error) {
                                    throw error
                                }
                            }

                            if(updatedFeatured === true) {
                                try {
                                    let notification = {
                                        type:       'entryFeature',
                                        docID:       entryID,
                                        title:       this.currentArtwork.content.title,
                                        createdBy:   'P83X2QiZAmUgQSOUvdU3FMezvl02', // Creatillo ID
                                        timestamp:   new Date(),
                                        recipients:  [ this.currentArtwork.creator.artist.id ]
                                    }

                                    await this.createNotification({
                                        notification: notification
                                    })
                                } catch(error) {
                                    throw error
                                }
                            }

                            this.$toasted.show(this.siteContent.entryUpdateSuccess)
                        } else {
                            this.$toasted.show(this.siteContent.entryUpdateError)
                        }

                        this.$emit('finishEditing')
                    }
                })
            },

            async deleteEntry() {
                this.destroy.status = true

                // Delete comments and replies
                try {
                    var commentsRef = await db
                        .collection('entryComments')
                        .where('doc', '==', this.currentEntryId)
                        .get()

                    var commentsData = commentsRef.docs.map(comment => ({
                        id: comment.id
                    }))
                    
                    for(var i = 0; i < commentsRef.size; i++) {
                        var repliesRef = await db
                            .collection('entryComments')
                            .doc(commentsData[i].id)
                            .collection('replies')
                            .get()

                            var repliesData = repliesRef.docs.map(reply => ({
                                id: reply.id
                            }))

                            for(var j = 0; j < repliesRef.size; j++) {
                                await db
                                    .collection('entryComments')
                                    .doc(commentsData[i].id)
                                    .collection('replies')
                                    .doc(repliesData[j].id)
                                    .delete()

                                console.log('reply deleted')
                            }
                        
                        await db
                            .collection('entryComments')
                            .doc(commentsData[i].id)
                            .delete()

                        console.log('comment deleted')
                    }
                } catch(error) {
                    console.log(error)
                }

                // Get entry images for deletion
                let images = []
                if(this.currentArtwork.media.carousel) {
                    for(let i = 0; i < this.currentArtwork.media.carousel.length; i++) {
                        images.push(this.currentArtwork.media.carousel[i].url)
                    }
                } else if(this.currentArtwork.media.slider) {
                    images.push(this.currentArtwork.media.slider.left.url)
                    images.push(this.currentArtwork.media.slider.right.url)
                }
                

                // Delete entry
                var result = await this.deleteJournalEntry({
                    id:      this.currentEntryId,
                    images:  images
                })

                if(result) {
                    this.$toasted.show(this.siteContent.entryDeleteSuccess)
                    this.$router.push('/creator/' + this.currentArtwork.creator.artist.slug)
                } else {
                    this.$toasted.show(this.siteContent.entryDeleteError)
                }
            }
        }
    }
</script>

<style scoped lang="scss">
    @import '../../scss/variables';

    .editorView {
        position: absolute;
        width: 100%;
        padding: 4rem 6rem 2rem 6rem;
    }

    .localizedFieldsGroup {
        position: relative;
        transition: $transition-eio-60;
    }

    .localizedFields {
        position: absolute;
        padding-bottom: 2rem;
        width: 100%;
        top: 0;
        left: 0;
    }

    .title {
        &:first-of-type {
            margin-right: 0.125rem;
        }

        &:last-of-type {
            margin-left: 0.125rem;
        }
    }

    .detailInput {
        margin-bottom: 2rem;
    }

    .horizontalFields {
        display: flex;
    }

    .relatedFields {
        .detailInput {
            margin-bottom: 0.25rem;

            &:last-of-type {
                margin-bottom: 2rem;
            }
        }
    }

    .buttonsWrapper {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        padding: 1rem;
        backdrop-filter: blur(2rem);
        background: var(--color-white-40);
        z-index: 10;
        display: flex;
        justify-content: center;
        border-radius: 1.5rem 1.5rem 0 0;
    }

    .buttons {
        max-width: 72rem;
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
    }

    .button {
        &:first-child {
            margin-right: 1rem;
        }
    }

    .editEntryOptions {
        display: flex;
    }

    .cancelEditButton {
        margin-right: 1rem;
    }

    .toggleView {
        display: flex;
        align-self: center;
        width: 100%;
        padding: 0.4rem 0.2rem;
        margin-bottom: 3rem;
        background: var(--color-grey-100);
        border-radius: 1rem;
    }

    .toggleItem {
        display: flex;
        justify-content: center;
        align-items: center;
        flex: 1;
        text-align: center;
        padding: 1.25rem;
        margin: 0 0.2rem;
        font-weight: 700;
        font-family: $font-secondary;
        border-radius: 1rem;
        color: var(--color-black);
        user-select: none;
        font-size: 1.1rem;
        line-height: 1.75rem;
        cursor: pointer;
        
        &:hover {
            background: var(--color-grey-150);
        }

        &:focus {
            outline: 0;
            box-shadow: var(--shadow-focus-s);
        }
    }

    .isToggleActive {
        background: var(--color-grey-150);
        font-size: 1.25rem;
    }

    .toggleTile {
        border-radius: 1rem;
        background: var(--color-white-40);
        backdrop-filter: blur(2rem);
        padding: 1.5rem;

        &.first {
            margin: 3rem 0 0.5rem 0;
        }
    }

    @media all and (max-width: 1440px) {
        .editorView {
            padding: 4rem 2rem 0 2rem;
        }
    }

    @media all and (max-width: 1200px) {
        .editorView {
            padding: 4rem 1rem 0 1rem;
        }
    }

    @media all and (max-width: 960px) {
        .horizontalFields {
            flex-direction: column;
        }

        .title {
            &:first-of-type {
                margin: 0 0 0.5rem 0;
            }

            &:last-of-type {
                margin-left: 0;
            }
        }
    }

    @media all and (max-width: 640px) {
        .editorView {
            padding: 2rem 0.5rem 2rem 0.5rem;
        }

        .buttonsWrapper {
            padding: 0.5rem;
            padding-bottom: calc(0.5rem + env(safe-area-inset-bottom));

            .deleteEntryButton {
                padding-right: 1rem;
            }
        }

        .editEntryOptions {
            justify-content: space-between;
        }

        .deleteText {
            display: none;
        }

        .editorView {
            padding: 2rem 0.5rem 0 0.5rem;
        }
    }
</style>